import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";
import { Chart, registerables } from "chart.js";
import {
  FiDollarSign,
  FiTrendingUp,
  FiUsers,
  FiMail,
  FiRefreshCw,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiLayers,
  FiShield,
  FiArrowUpRight,
  FiActivity,
  FiBox,
  FiServer,
  FiCheck,
  FiAlertCircle,
  FiCalendar,
  FiBarChart2,
} from "react-icons/fi";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";

Chart.register(...registerables);

// Fallback token provided for super admin dashboard access if not present in session
const DEFAULT_SUPERADMIN_TOKEN =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjZhZDFmODRhOTkwYTM0ZGRiOGY1MjBjNiIsInJvbGUiOiI2YWQxZjBjMzYyOTg2YTg5OGZhYjJhNTEiLCJ0ZW5hbnRJZCI6bnVsbCwiaXNTdXBlckFkbWluIjp0cnVlLCJpYXQiOjE3OTIxNDU3MjYsImV4cCI6MTc5Mjg2NTcyNn0.olRB7penvUuxOrRQOb3BBzuCe0fZN8mG98Z7_PXzbdw";

// Format currency
const formatUSD = (val = 0) =>
  "$" +
  Number(val || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Format ISO date
const formatDate = (isoString) => {
  if (!isoString) return "-";
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoString;
  }
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [chartMetric, setChartMetric] = useState("revenue"); // "revenue" | "transactions"
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  // Fetch Dashboard Stats strictly from super-admin/dashboard/stats
  const fetchDashboardStats = useCallback(async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
      loader(true);
    }

    try {
      if (!localStorage.getItem("token")) {
        localStorage.setItem("token", DEFAULT_SUPERADMIN_TOKEN);
      }

      const res = await ApiClient.get("super-admin/dashboard/stats");
      if (res?.success && res?.data) {
        setStats(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch super-admin/dashboard/stats:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
      loader(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  // Render Modern 6-Month Chart strictly from API charts.revenueTrend6m
  useEffect(() => {
    if (!chartRef.current || !stats?.charts?.revenueTrend6m) return;

    const trend = stats.charts.revenueTrend6m || [];
    const labels = trend.map((t) => {
      if (!t.month) return "";
      const [year, month] = t.month.split("-");
      const monthNames = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
      ];
      return `${monthNames[parseInt(month, 10) - 1] || month} ${year ? "'" + year.slice(2) : ""}`;
    });

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    const ctx = chartRef.current.getContext("2d");

    let datasets = [];
    if (chartMetric === "revenue") {
      const netGradient = ctx.createLinearGradient(0, 0, 0, 240);
      netGradient.addColorStop(0, "rgba(17, 91, 157, 0.35)");
      netGradient.addColorStop(1, "rgba(17, 91, 157, 0.0)");

      const grossGradient = ctx.createLinearGradient(0, 0, 0, 240);
      grossGradient.addColorStop(0, "rgba(5, 150, 105, 0.22)");
      grossGradient.addColorStop(1, "rgba(5, 150, 105, 0.0)");

      datasets = [
        {
          label: "Net Revenue ($)",
          data: trend.map((t) => t.netRevenue ?? 0),
          backgroundColor: netGradient,
          borderColor: "#115B9D",
          borderWidth: 3,
          tension: 0.35,
          fill: true,
          pointBackgroundColor: "#115B9D",
          pointBorderColor: "#FFFFFF",
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: "Gross Volume ($)",
          data: trend.map((t) => t.grossVolume ?? 0),
          backgroundColor: grossGradient,
          borderColor: "#059669",
          borderWidth: 2,
          borderDash: [5, 4],
          tension: 0.35,
          fill: true,
          pointBackgroundColor: "#059669",
          pointBorderColor: "#FFFFFF",
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ];
    } else {
      datasets = [
        {
          label: "Completed Transactions",
          data: trend.map((t) => t.completedTransactions ?? 0),
          backgroundColor: "#10B981",
          borderRadius: 6,
          barThickness: "flex",
          maxBarThickness: 24,
        },
        {
          label: "Total Transactions",
          data: trend.map((t) => t.totalTransactions ?? 0),
          backgroundColor: "#115B9D",
          borderRadius: 6,
          barThickness: "flex",
          maxBarThickness: 24,
        },
        {
          label: "Refunded Transactions",
          data: trend.map((t) => t.refundedTransactions ?? 0),
          backgroundColor: "#F59E0B",
          borderRadius: 6,
          barThickness: "flex",
          maxBarThickness: 24,
        },
        {
          label: "Failed Transactions",
          data: trend.map((t) => t.failedTransactions ?? 0),
          backgroundColor: "#E11D48",
          borderRadius: 6,
          barThickness: "flex",
          maxBarThickness: 24,
        },
      ];
    }

    chartInstanceRef.current = new Chart(ctx, {
      type: chartMetric === "revenue" ? "line" : "bar",
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          intersect: false,
        },
        plugins: {
          legend: {
            position: "top",
            align: "end",
            labels: {
              boxWidth: 8,
              boxHeight: 8,
              usePointStyle: true,
              pointStyle: "circle",
              padding: 12,
              color: "#0F172A",
              font: { weight: "700", size: 11 },
            },
          },
          tooltip: {
            backgroundColor: "#0B2545",
            titleColor: "#FFFFFF",
            bodyColor: "#F8FAFC",
            titleFont: { weight: "800", size: 12 },
            bodyFont: { weight: "600", size: 11 },
            padding: 12,
            cornerRadius: 10,
            boxPadding: 4,
            callbacks: {
              label: (context) => {
                const label = context.dataset.label || "";
                const val = context.raw ?? 0;
                return chartMetric === "revenue"
                  ? `${label}: ${formatUSD(val)}`
                  : `${label}: ${val}`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: "#1E293B",
              font: { weight: "700", size: 10 },
            },
          },
          y: {
            grid: { color: "rgba(226, 232, 240, 0.8)" },
            ticks: {
              color: "#1E293B",
              font: { weight: "700", size: 10 },
              callback: (v) => (chartMetric === "revenue" ? `$${v}` : v),
            },
          },
        },
      },
    });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [stats, chartMetric]);

  // Extract exact fields from API response
  const counters = stats?.counters || {};
  const tenants = counters?.tenants || {};
  const financials = counters?.financials || {};
  const qb = stats?.quickbooksOverview || {};
  const qbDist = qb?.distribution || {};
  const qbHealth = qb?.syncHealth || {};
  const recentTransactions = stats?.recentActivity?.transactions || [];
  const recentInquiries = stats?.recentActivity?.inquiries || [];
  const recentTenants = stats?.recentActivity?.tenants || [];

  return (
    <Layout>
      <div className="w-full max-w-full overflow-x-hidden space-y-5 sm:space-y-6 pb-12 antialiased">

        {/* Loading Spinner */}
        {loading && !stats ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-slate-200 shadow-sm">
            <div className="w-9 h-9 border-3 border-[#115B9D] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm font-bold text-slate-800">Loading Dashboard Statistics...</p>
          </div>
        ) : null}

        {stats ? (
          <>
            {/* ======================================================== */}
            {/* 1. HERO FINANCIAL OVERVIEW (COLORFUL DASHBOARD BANNER) */}
            {/* ======================================================== */}
            <div className="bg-gradient-to-br from-[#071324] via-[#0E2C52] to-[#115B9D] rounded-2xl p-5 sm:p-7 text-white shadow-[0_10px_30px_rgba(7,19,36,0.18)] border border-slate-800 relative overflow-hidden space-y-6">

              {/* Subtle ambient background glow */}
              <div className="absolute -top-16 -right-16 w-64 h-64 bg-sky-400/15 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl pointer-events-none" />

              {/* Banner Top Row: Dashboard Title, Live Gateway Pill & Refresh Button */}
              <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-5 border-b border-white/10">
                <div className="space-y-1">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                      Dashboard
                    </h1>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-[11px] font-bold text-sky-200 uppercase tracking-wide">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      <FiShield className="w-3.5 h-3.5 text-sky-200" />
                      Super Admin Gateway
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-sky-100/80 font-normal">
                    Platform operations, tenant metrics, and billing overview
                  </p>
                </div>

                <button
                  onClick={() => fetchDashboardStats(true)}
                  disabled={refreshing || loading}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 active:bg-white/25 text-white text-xs font-bold transition-all border border-white/20 backdrop-blur-md shadow-sm disabled:opacity-60 cursor-pointer self-stretch sm:self-auto justify-center"
                >
                  <FiRefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                  <span>{refreshing ? "Refreshing..." : "Refresh Stats"}</span>
                </button>
              </div>

              {/* Banner Bottom Row: Net Revenue Hero & Metric Pills Grid */}
              <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6 pt-1">

                {/* Net Revenue Hero Display */}
                <div className="space-y-1.5 max-w-md">
                  <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/25 text-xs font-bold uppercase tracking-wider text-emerald-300">
                    <FiDollarSign className="w-3.5 h-3.5 text-emerald-400" />
                    Net Platform Revenue
                  </div>
                  <div className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight">
                    {formatUSD(financials?.netRevenue)}
                  </div>
                  <p className="text-xs text-sky-100/75 font-normal">
                    Total completed transaction volume after refunds and adjustments.
                  </p>
                </div>

                {/* Right: Modern Bento Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full lg:w-auto">

                  {/* Gross Volume */}
                  <div className="bg-white/10 backdrop-blur-md p-3.5 rounded-xl border border-white/15 shadow-xs">
                    <div className="text-[11px] font-extrabold uppercase tracking-wider text-sky-200">
                      Gross Volume
                    </div>
                    <div className="text-lg sm:text-xl font-black text-white mt-1">
                      {formatUSD(financials?.grossRevenue)}
                    </div>
                  </div>

                  {/* Estimated MRR */}
                  <div className="bg-white/10 backdrop-blur-md p-3.5 rounded-xl border border-white/15 shadow-xs">
                    <div className="text-[11px] font-extrabold uppercase tracking-wider text-sky-200">
                      Estimated MRR
                    </div>
                    <div className="text-lg sm:text-xl font-black text-white mt-1">
                      {formatUSD(financials?.estimatedMRR)}
                    </div>
                  </div>

                  {/* Completed Transactions */}
                  <div className="bg-white/10 backdrop-blur-md p-3.5 rounded-xl border border-white/15 shadow-xs">
                    <div className="text-[11px] font-extrabold uppercase tracking-wider text-emerald-300">
                      Completed Txns
                    </div>
                    <div className="text-lg sm:text-xl font-black text-white mt-1">
                      {financials?.completedTransactions ?? 0}
                    </div>
                  </div>

                  {/* Total Transactions */}
                  <div className="bg-white/10 backdrop-blur-md p-3.5 rounded-xl border border-white/15 shadow-xs">
                    <div className="text-[11px] font-extrabold uppercase tracking-wider text-sky-200">
                      Total Txns
                    </div>
                    <div className="text-lg sm:text-xl font-black text-white mt-1">
                      {financials?.totalTransactions ?? 0}
                    </div>
                  </div>

                </div>

              </div>
            </div>

            {/* ======================================================== */}
            {/* 2. PLATFORM KPI COUNTER CARDS (4 SLEEK BENTO TILES) */}
            {/* ======================================================== */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

              {/* Tile 1: Tenants */}
              <div
                onClick={() => navigate("/tenants")}
                className="bg-white rounded-2xl p-5 border border-slate-200/90 shadow-[0_2px_10px_rgba(0,0,0,0.03)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.06)] transition-all flex flex-col justify-between cursor-pointer group hover:border-indigo-400/50"
              >
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-xl bg-indigo-50 border border-indigo-200 text-indigo-700 flex items-center justify-center shadow-xs">
                    <HiOutlineBuildingOffice2 className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] font-black uppercase tracking-wider text-indigo-800 bg-indigo-50 px-2.5 py-0.5 rounded-md border border-indigo-200">
                    {tenants?.active ?? 0} Active
                  </span>
                </div>
                <div className="mt-4">
                  <div className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                    {tenants?.total ?? 0}
                  </div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mt-1">
                    Total Tenants
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-medium text-slate-700">
                  <span>Trial / Suspended:</span>
                  <span className="text-slate-900 font-bold">
                    {tenants?.trial ?? 0} Trial &bull; {tenants?.suspended ?? 0} Suspended
                  </span>
                </div>
              </div>

              {/* Tile 2: Plans */}
              <div
                onClick={() => navigate("/plans")}
                className="bg-white rounded-2xl p-5 border border-slate-200/90 shadow-[0_2px_10px_rgba(0,0,0,0.03)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.06)] transition-all flex flex-col justify-between cursor-pointer group hover:border-[#115B9D]/40"
              >
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-200 text-[#115B9D] flex items-center justify-center shadow-xs">
                    <FiLayers className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] font-black uppercase tracking-wider text-[#115B9D] bg-blue-50 px-2.5 py-0.5 rounded-md border border-blue-200">
                    Tier Catalog
                  </span>
                </div>
                <div className="mt-4">
                  <div className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                    {counters?.plans ?? 0}
                  </div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mt-1">
                    Subscription Plans
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-medium text-slate-700">
                  <span>Status:</span>
                  <span className="text-emerald-700 font-bold">Active in Catalog</span>
                </div>
              </div>

              {/* Tile 3: Unread Inquiries */}
              <div
                onClick={() => navigate("/contact-us")}
                className="bg-white rounded-2xl p-5 border border-slate-200/90 shadow-[0_2px_10px_rgba(0,0,0,0.03)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.06)] transition-all flex flex-col justify-between cursor-pointer group hover:border-amber-400/50"
              >
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform">
                    <FiMail className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] font-black uppercase tracking-wider text-amber-900 bg-amber-50 px-2.5 py-0.5 rounded-md border border-amber-300">
                    Needs Attention
                  </span>
                </div>
                <div className="mt-4">
                  <div className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                    {counters?.unreadInquiries ?? 0}
                  </div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mt-1 flex items-center justify-between">
                    <span>Unread Inquiries</span>
                    <span className="text-[#115B9D] opacity-0 group-hover:opacity-100 transition-opacity text-xs">&rarr;</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-medium text-slate-700">
                  <span>Source:</span>
                  <span className="text-slate-900 font-bold">Tenant Support</span>
                </div>
              </div>

              {/* Tile 4: Sub Admins */}
              <div
                onClick={() => navigate("/staff")}
                className="bg-white rounded-2xl p-5 border border-slate-200/90 shadow-[0_2px_10px_rgba(0,0,0,0.03)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.06)] transition-all flex flex-col justify-between cursor-pointer group hover:border-slate-400/60"
              >
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-xl bg-slate-100 border border-slate-200 text-slate-800 flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform">
                    <FiUsers className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] font-black uppercase tracking-wider text-slate-800 bg-slate-100 px-2.5 py-0.5 rounded-md border border-slate-200">
                    Authorized
                  </span>
                </div>
                <div className="mt-4">
                  <div className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                    {counters?.subAdmins ?? 0}
                  </div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mt-1 flex items-center justify-between">
                    <span>Sub Administrators</span>
                    <span className="text-[#115B9D] opacity-0 group-hover:opacity-100 transition-opacity text-xs">&rarr;</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-medium text-slate-700">
                  <span>Role:</span>
                  <span className="text-slate-900 font-bold">Platform Ops</span>
                </div>
              </div>

            </div>

            {/* ======================================================== */}
            {/* 3. 6-MONTH PERFORMANCE VELOCITY CHART */}
            {/* ======================================================== */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 border border-slate-200/90 shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-100">
                <div>
                  <h2 className="text-lg font-black text-slate-900 tracking-tight">
                    Revenue & Transaction Velocity (6 Months)
                  </h2>
                  <p className="text-xs text-slate-700 font-medium mt-0.5">
                    Monthly net revenue, gross volume, and transaction trends
                  </p>
                </div>

                <div className="inline-flex p-1 bg-slate-100 rounded-xl border border-slate-200 self-start sm:self-auto">
                  <button
                    onClick={() => setChartMetric("revenue")}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${chartMetric === "revenue"
                      ? "bg-[#115B9D] text-white shadow-xs"
                      : "text-slate-700 hover:text-slate-900"
                      }`}
                  >
                    Revenue ($)
                  </button>
                  <button
                    onClick={() => setChartMetric("transactions")}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${chartMetric === "transactions"
                      ? "bg-[#115B9D] text-white shadow-xs"
                      : "text-slate-700 hover:text-slate-900"
                      }`}
                  >
                    Transactions
                  </button>
                </div>
              </div>

              <div className="h-64 sm:h-72 w-full pt-4">
                <canvas ref={chartRef} />
              </div>
            </div>

            {/* ======================================================== */}
            {/* 4. QUICKBOOKS OVERVIEW (DISTRIBUTION & SYNC HEALTH) */}
            {/* ======================================================== */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 border border-slate-200/90 shadow-[0_2px_10px_rgba(0,0,0,0.03)] space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3.5 border-b border-slate-100">
                <div>
                  <h2 className="text-lg font-black text-slate-900 tracking-tight flex items-center gap-2">
                    <FiServer className="w-5 h-5 text-[#115B9D]" />
                    QuickBooks Overview
                  </h2>
                  <p className="text-xs text-slate-700 font-medium mt-0.5">
                    Tenant accounting sync metrics and distribution
                  </p>
                </div>
                <div className="text-xs font-bold text-slate-800 px-3 py-1 rounded-full bg-slate-100 border border-slate-200 self-start sm:self-auto">
                  Total Tenants: {qb?.totalTenants ?? 0}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

                {/* Distribution Segmented Progress Meter */}
                <div className="space-y-3.5 p-4 sm:p-5 rounded-xl bg-slate-50/80 border border-slate-200">
                  <div className="text-xs font-black uppercase text-slate-800 tracking-wider">
                    Tenant Distribution
                  </div>

                  <div>
                    <div className="flex justify-between text-xs font-bold text-slate-800 mb-1.5">
                      <span>QBO Only (Online)</span>
                      <span className="font-extrabold">{qbDist?.qboOnly?.percentage ?? 0}% ({qbDist?.qboOnly?.count ?? 0})</span>
                    </div>
                    <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#115B9D] rounded-full transition-all duration-500"
                        style={{ width: `${qbDist?.qboOnly?.percentage ?? 0}%` }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-xs font-bold text-slate-800 mb-1.5">
                      <span>QBD Only (Desktop)</span>
                      <span className="font-extrabold">{qbDist?.qbdOnly?.percentage ?? 0}% ({qbDist?.qbdOnly?.count ?? 0})</span>
                    </div>
                    <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-600 rounded-full transition-all duration-500"
                        style={{ width: `${qbDist?.qbdOnly?.percentage ?? 0}%` }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-xs font-bold text-slate-800 mb-1.5">
                      <span>Hybrid (Both QBO + QBD)</span>
                      <span className="font-extrabold">{qbDist?.hybridBoth?.percentage ?? 0}% ({qbDist?.hybridBoth?.count ?? 0})</span>
                    </div>
                    <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-600 rounded-full transition-all duration-500"
                        style={{ width: `${qbDist?.hybridBoth?.percentage ?? 0}%` }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-xs font-bold text-slate-800 mb-1.5">
                      <span>None / Pending</span>
                      <span className="font-extrabold">{qbDist?.noneOrPending?.percentage ?? 0}% ({qbDist?.noneOrPending?.count ?? 0})</span>
                    </div>
                    <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-slate-500 rounded-full transition-all duration-500"
                        style={{ width: `${qbDist?.noneOrPending?.percentage ?? 0}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Sync Health Badges Grid */}
                <div className="space-y-3 p-4 sm:p-5 rounded-xl bg-slate-50/80 border border-slate-200 flex flex-col justify-between">
                  <div className="text-xs font-black uppercase text-slate-800 tracking-wider">
                    Sync Health
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3.5 bg-white rounded-xl border border-slate-200 text-center shadow-xs">
                      <div className="text-2xl font-black text-emerald-700">
                        {qbHealth?.connected ?? 0}
                      </div>
                      <div className="text-[11px] font-bold text-slate-700 uppercase mt-0.5">
                        Connected
                      </div>
                    </div>

                    <div className="p-3.5 bg-white rounded-xl border border-slate-200 text-center shadow-xs">
                      <div className="text-2xl font-black text-amber-700">
                        {qbHealth?.disconnectedOrPending ?? 0}
                      </div>
                      <div className="text-[11px] font-bold text-slate-700 uppercase mt-0.5">
                        Pending / Disc.
                      </div>
                    </div>

                    <div className="p-3.5 bg-white rounded-xl border border-slate-200 text-center shadow-xs">
                      <div className="text-2xl font-black text-[#115B9D]">
                        {qbHealth?.lastSyncSuccessful ?? 0}
                      </div>
                      <div className="text-[11px] font-bold text-slate-700 uppercase mt-0.5">
                        Last Sync OK
                      </div>
                    </div>

                    <div className="p-3.5 bg-white rounded-xl border border-slate-200 text-center shadow-xs">
                      <div className="text-2xl font-black text-rose-700">
                        {qbHealth?.lastSyncErrors ?? 0}
                      </div>
                      <div className="text-[11px] font-bold text-slate-700 uppercase mt-0.5">
                        Sync Errors
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* ======================================================== */}
            {/* 5. RECENT ACTIVITY: TRANSACTIONS & INQUIRIES */}
            {/* ======================================================== */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 items-start">

              {/* Transactions Card */}
              <div className="bg-white rounded-2xl p-4 sm:p-6 border border-slate-200/90 shadow-[0_2px_10px_rgba(0,0,0,0.03)] space-y-4">
                <div className="flex items-center justify-between pb-3.5 border-b border-slate-100">
                  <div>
                    <h2 className="text-lg font-black text-slate-900 tracking-tight flex items-center gap-2">
                      <FiCreditCard className="w-5 h-5 text-[#115B9D]" />
                      Recent Transactions
                    </h2>
                    <p className="text-xs text-slate-700 font-medium mt-0.5">
                      Platform payments and order records
                    </p>
                  </div>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-800 border border-slate-200">
                    {recentTransactions.length} Items
                  </span>
                </div>

                {/* RESPONSIVE LISTING ROWS WITH OPTIMIZED VERTICAL SCROLL */}
                <div
                  className={`space-y-2.5 ${
                    recentTransactions.length > 3
                      ? "max-h-[340px] sm:max-h-[258px] overflow-y-auto pr-1.5 dashboard-scrollbar"
                      : ""
                  }`}
                >
                  {recentTransactions.length > 0 ? (
                    recentTransactions.map((tx) => (
                      <div
                        key={tx.id || tx.orderId}
                        className="p-3 sm:p-3.5 rounded-xl bg-slate-50/70 hover:bg-slate-100/70 transition-colors border border-slate-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5"
                      >
                        {/* Left: Order # + Customer & Tenant */}
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-200 text-[#115B9D] font-black text-xs flex items-center justify-center shrink-0">
                            #{tx.orderId}
                          </div>
                          <div className="min-w-0">
                            <div className="font-bold text-xs sm:text-sm text-slate-900 truncate">
                              {tx.customerName || "-"}
                            </div>
                            <div className="text-[11px] text-slate-600 font-medium flex items-center gap-1.5 flex-wrap mt-0.5">
                              <span>
                                Tenant: <strong className="text-slate-800">{tx.tenantName || "Direct"}</strong>
                              </span>
                              <span className="text-slate-400">•</span>
                              <span className="uppercase text-[10px] font-bold text-slate-700 bg-white px-1.5 py-0.2 rounded border border-slate-200">
                                {tx.paymentMethod}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Right: Amount, Status & Date */}
                        <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-1 shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-200/60">
                          <div className="font-black text-sm sm:text-base text-slate-900">
                            {formatUSD(tx.amount)}
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${tx.status === "completed"
                                ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                                : "bg-amber-100 text-amber-900 border border-amber-300"
                                }`}
                            >
                              {tx.status === "completed" ? (
                                <FiCheckCircle className="w-3 h-3 text-emerald-700" />
                              ) : (
                                <FiClock className="w-3 h-3 text-amber-700" />
                              )}
                              {tx.status}
                            </span>
                            <span className="text-[11px] text-slate-600 font-medium whitespace-nowrap">
                              {formatDate(tx.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="py-8 text-center text-slate-600 font-medium text-xs">
                      No transactions available in API response.
                    </div>
                  )}
                </div>
              </div>

              {/* Inquiries Card */}
              <div className="bg-white rounded-2xl p-4 sm:p-6 border border-slate-200/90 shadow-[0_2px_10px_rgba(0,0,0,0.03)] space-y-4">
                <div className="flex items-center justify-between pb-3.5 border-b border-slate-100">
                  <div>
                    <h2 className="text-lg font-black text-slate-900 tracking-tight flex items-center gap-2">
                      <FiMail className="w-5 h-5 text-amber-800" />
                      Recent Inquiries
                    </h2>
                    <p className="text-xs text-slate-700 font-medium mt-0.5">
                      Tenant support and contact submissions
                    </p>
                  </div>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-800 border border-slate-200">
                    {recentInquiries.length} Items
                  </span>
                </div>

                {/* RESPONSIVE LISTING ROWS WITH OPTIMIZED VERTICAL SCROLL */}
                <div
                  className={`space-y-2.5 ${
                    recentInquiries.length > 3
                      ? "max-h-[340px] sm:max-h-[258px] overflow-y-auto pr-1.5 dashboard-scrollbar"
                      : ""
                  }`}
                >
                  {recentInquiries.length > 0 ? (
                    recentInquiries.map((inq) => (
                      <div
                        key={inq.id}
                        className="p-3 sm:p-3.5 rounded-xl bg-slate-50/70 hover:bg-slate-100/70 transition-colors border border-slate-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5"
                      >
                        {/* Left: Mail icon + Email & Scope */}
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 font-black text-xs flex items-center justify-center shrink-0">
                            <FiMail className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-bold text-xs sm:text-sm text-slate-900 truncate" title={inq.email}>
                              {inq.email}
                            </div>
                            <div className="text-[11px] text-slate-600 font-medium flex items-center gap-1.5 mt-0.5">
                              <span>Scope:</span>
                              <span className="text-[10px] font-bold uppercase text-slate-700 bg-white px-1.5 py-0.5 rounded border border-slate-200">
                                {inq.tenantName || "Global"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Right: Status badge & Date */}
                        <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-1 shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-200/60">
                          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase bg-amber-100 text-amber-900 border border-amber-300">
                            {inq.status}
                          </span>
                          <span className="text-[11px] text-slate-600 font-medium whitespace-nowrap">
                            {formatDate(inq.createdAt)}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="py-8 text-center text-slate-600 font-medium text-xs">
                      No inquiries available in API response.
                    </div>
                  )}
                </div>
              </div>

            </div>

            {/* ======================================================== */}
            {/* 6. RECENT ACTIVITY: TENANTS (if any in recentActivity.tenants) */}
            {/* ======================================================== */}
            {recentTenants.length > 0 ? (
              <div className="bg-white rounded-2xl p-4 sm:p-6 border border-slate-200/90 shadow-[0_2px_10px_rgba(0,0,0,0.03)] space-y-4">
                <div className="flex items-center justify-between pb-3.5 border-b border-slate-100">
                  <div>
                    <h2 className="text-lg font-black text-slate-900 tracking-tight flex items-center gap-2">
                      <HiOutlineBuildingOffice2 className="w-5 h-5 text-[#115B9D]" />
                      Recent Tenants
                    </h2>
                    <p className="text-xs text-slate-700 font-medium mt-0.5">
                      Recently registered tenant organizations
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/tenants")}
                    className="text-xs font-bold text-[#115B9D] hover:underline cursor-pointer"
                  >
                    View All &rarr;
                  </button>
                </div>

                {/* RESPONSIVE LISTING ROWS WITH OPTIMIZED VERTICAL SCROLL */}
                <div
                  className={`space-y-2.5 ${
                    recentTenants.length > 3
                      ? "max-h-[340px] sm:max-h-[258px] overflow-y-auto pr-1.5 dashboard-scrollbar"
                      : ""
                  }`}
                >
                  {recentTenants.map((t) => (
                    <div
                      key={t.id || t._id}
                      onClick={() => navigate(`/tenants/detail/${t.id || t._id}`)}
                      className="p-3 sm:p-3.5 rounded-xl bg-slate-50/70 hover:bg-slate-100/70 transition-colors border border-slate-200/80 flex items-center justify-between gap-3 cursor-pointer"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg bg-indigo-50 border border-indigo-200 text-indigo-700 font-black text-xs flex items-center justify-center shrink-0">
                          <HiOutlineBuildingOffice2 className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold text-xs sm:text-sm text-slate-900 truncate">
                            {t.name || t.tenantName || "-"}
                          </div>
                          <div className="text-[11px] text-slate-600 font-medium">
                            Created: {formatDate(t.createdAt)}
                          </div>
                        </div>
                      </div>

                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase bg-slate-100 text-slate-800 border border-slate-200 shrink-0">
                        {t.status || "-"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

          </>
        ) : null}

      </div>
    </Layout>
  );
}
